import { useState, useEffect } from 'react';
import { X, Package, Camera, CheckCircle2, AlertTriangle, Loader2, Info } from 'lucide-react';
import sprayService from '../../services/spray';
import purchaseOrderService from '../../services/purchaseOrders';
import BarcodeScanner from '../../components/shared/BarcodeScanner';
import nurseryService from '../../services/nursery';
import { LOCATION_TYPE_LABEL } from '../../types/nursery';
import type { NurseryLocation } from '../../types/nursery';
import type { ChemicalInventoryLocation } from '../../types/spray';
import type { PurchaseOrder, POLineItem } from '../../types/purchaseOrders';
import { toNumOr } from '../../utils/numeric';
import { ApiError } from '../../types/api';

/** What is still to come on a line, rounded off float noise. */
function outstandingOf(li: POLineItem): number {
  return parseFloat((toNumOr(li.quantity_ordered) - toNumOr(li.quantity_received)).toFixed(4));
}

/** More than is still outstanding: allowed (suppliers send extra), but asked. */
function overReceived(row: { quantity_received: string; line_item: POLineItem }): boolean {
  const qty = Number(row.quantity_received);
  return Number.isFinite(qty) && qty > outstandingOf(row.line_item);
}

/** Outcome of checking a scanned/typed barcode against the chemical registry. */
type BarcodeCheck =
  | { status: 'checking' }
  | { status: 'match'; message: string }
  | { status: 'mismatch'; message: string }
  | { status: 'unknown'; message: string };

const BARCODE_CHECK_STYLES: Record<BarcodeCheck['status'], string> = {
  checking: 'text-gray-400',
  match: 'text-green-700',
  mismatch: 'text-amber-700',
  unknown: 'text-gray-500',
};

interface ReceiveItemRow {
  line_item_id: number;
  line_item: POLineItem;
  quantity_received: string;
  location_id: string;
  /** A nursery line: the shop place (or nursery location) it was put in. */
  nursery_location_id: string;
  batch_number: string;
  expiry_date: string;
  barcode_scanned: string;
  barcode_check: BarcodeCheck | null;
  unit_cost: string;
  notes: string;
  include: boolean;
}

interface ReceiveStockModalProps {
  po: PurchaseOrder;
  onClose: () => void;
  onReceived: () => void;
}

export default function ReceiveStockModal({ po, onClose, onReceived }: ReceiveStockModalProps) {
  const [rows, setRows] = useState<ReceiveItemRow[]>(() =>
    po.line_items
      .filter(li => toNumOr(li.quantity_received) < toNumOr(li.quantity_ordered))
      .map(li => ({
        line_item_id: li.id,
        line_item: li,
        quantity_received: String(parseFloat((toNumOr(li.quantity_ordered) - toNumOr(li.quantity_received)).toFixed(4))),
        location_id: '',
        nursery_location_id: '',
        batch_number: '',
        expiry_date: '',
        barcode_scanned: '',
        barcode_check: null,
        unit_cost: li.unit_cost ? String(li.unit_cost) : '',
        notes: '',
        include: true,
      }))
  );
  const [receiptNotes, setReceiptNotes] = useState('');
  // Freight on the delivery, ex GST: spread into the landed cost of nursery lines.
  const [freight, setFreight] = useState('');
  const [locations, setLocations] = useState<ChemicalInventoryLocation[]>([]);
  const [places, setPlaces] = useState<NurseryLocation[]>([]);
  const hasNursery = po.line_items.some(li => li.item_type === 'nursery');
  const [scanningRowId, setScanningRowId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (po.line_items.some(li => li.item_type === 'chemical')) {
      purchaseOrderService.getLocations().then(res => {
        if (res.success && res.data) setLocations(res.data);
      }).catch(() => { /* no chemical store to offer */ });
    }
    if (po.line_items.some(li => li.item_type === 'nursery')) {
      // Shop places first: they are where a delivery usually goes.
      nurseryService.getLocations({ active: true }).then(res => {
        if (!res.success) return;
        const rank = (l: NurseryLocation) => (l.location_type === 'shop_floor' ? 0 : l.location_type === 'storeroom' ? 1 : 2);
        setPlaces([...(res.data || [])].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name)));
      }).catch(() => { /* no places to offer */ });
    }
  }, [po.line_items]);

  function updateRow(lineItemId: number, patch: Partial<ReceiveItemRow>) {
    setRows(prev => prev.map(r => r.line_item_id === lineItemId ? { ...r, ...patch } : r));
  }

  function handleBarcodeScanned(lineItemId: number, barcode: string) {
    setScanningRowId(null);
    updateRow(lineItemId, { barcode_scanned: barcode });
    verifyBarcode(lineItemId, barcode);
  }

  /**
   * Look the barcode up in the chemical registry and tell the user whether it
   * actually matches the chemical this PO line ordered. Advisory only — a
   * mismatch never blocks the receipt, since substitutes do get delivered.
   */
  async function verifyBarcode(lineItemId: number, barcode: string) {
    const code = barcode.trim();
    if (!code) {
      updateRow(lineItemId, { barcode_check: null });
      return;
    }

    const lineItem = po.line_items.find(li => li.id === lineItemId);
    if (!lineItem) return;

    // Nursery stock: the code names one of the shop's items, or none yet, in
    // which case receiving remembers it for this item as this supplier's.
    if (lineItem.item_type === 'nursery') {
      updateRow(lineItemId, { barcode_check: { status: 'checking' } });
      try {
        const res = await nurseryService.findStockItemByBarcode(code);
        const found = res.success ? res.data : null;
        if (!found) throw new Error('none');
        updateRow(lineItemId, {
          barcode_check: found.id === lineItem.stock_item_id
            ? { status: 'match', message: 'Matches this line.' }
            : { status: 'mismatch', message: `That barcode is ${found.display_name || found.name || 'another item'}, not this line.` },
        });
      } catch {
        updateRow(lineItemId, {
          barcode_check: { status: 'unknown', message: 'A new barcode: it will be remembered for this item, as this supplier\'s.' },
        });
      }
      return;
    }

    // General supplies have no registry to check against — the barcode is still
    // stored on the receipt item for audit, so just confirm that.
    if (lineItem.item_type !== 'chemical') {
      updateRow(lineItemId, {
        barcode_check: { status: 'unknown', message: 'Recorded against this line.' },
      });
      return;
    }

    updateRow(lineItemId, { barcode_check: { status: 'checking' } });

    let match = null;
    try {
      const res = await sprayService.getChemicalByBarcode(code);
      match = res.success && res.data?.length ? res.data[0] : null;
    } catch {
      updateRow(lineItemId, {
        barcode_check: { status: 'unknown', message: 'Could not check this barcode. It will still be recorded.' },
      });
      return;
    }

    const expectedName = lineItem.product_name ?? lineItem.item_description ?? 'this line item';

    if (!match) {
      updateRow(lineItemId, {
        barcode_check: { status: 'unknown', message: 'Not in the chemical registry — recorded as-is.' },
      });
    } else if (lineItem.chemical_id == null) {
      updateRow(lineItemId, {
        barcode_check: {
          status: 'unknown',
          message: `Identified as ${match.product_name} — no registry chemical linked to this line to check against.`,
        },
      });
    } else if (match.id === lineItem.chemical_id) {
      const pack = match.matched_pack_size
        ? ` (${match.matched_pack_size}${match.matched_pack_unit ?? ''} pack)`
        : '';
      updateRow(lineItemId, {
        barcode_check: { status: 'match', message: `Matches ${match.product_name}${pack}.` },
      });
    } else {
      updateRow(lineItemId, {
        barcode_check: {
          status: 'mismatch',
          message: `Scanned ${match.product_name}, but this line ordered ${expectedName}.`,
        },
      });
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const includedRows = rows.filter(r => r.include);
    if (!includedRows.length) {
      setError('Please select at least one item to receive.');
      return;
    }

    for (const row of includedRows) {
      if (row.line_item.item_type === 'chemical' && !row.location_id) {
        setError(`Please select a storage location for "${row.line_item.product_name ?? row.line_item.item_description}".`);
        return;
      }
      const qty = Number(row.quantity_received);
      if (!row.quantity_received.trim() || !Number.isFinite(qty) || qty <= 0) {
        setError('Please enter a valid received quantity for all selected items.');
        return;
      }
      if (row.line_item.item_type === 'nursery') {
        if (!row.nursery_location_id) {
          setError(`Choose where "${row.line_item.item_description}" was put.`);
          return;
        }
        if (!Number.isInteger(qty)) {
          setError('Plants and merchandise are received in whole numbers.');
          return;
        }
      }
    }
    const freightValue = freight.trim() === '' ? null : Number(freight.replace(/[$,\s]/g, ''));
    if (freightValue != null && (!Number.isFinite(freightValue) || freightValue < 0)) {
      setError('Enter the freight as a number, such as 25.00');
      return;
    }

    const over = includedRows.filter(overReceived);
    if (over.length) {
      const lines = over.map(r =>
        `${r.line_item.product_name ?? r.line_item.item_description}: ${r.quantity_received} received, ${outstandingOf(r.line_item)} ${r.line_item.unit} outstanding`,
      );
      if (!confirm(`More than was ordered:\n\n${lines.join('\n')}\n\nRecord it anyway?`)) return;
    }

    setSubmitting(true);
    try {
      const res = await purchaseOrderService.receiveStock(po.id, {
        notes: receiptNotes.trim() || null,
        ...(hasNursery && freightValue != null ? { freight_ex_gst: freightValue } : {}),
        items: includedRows.map(row => ({
          line_item_id: row.line_item_id,
          chemical_id: row.line_item.item_type === 'chemical' ? row.line_item.chemical_id ?? null : null,
          quantity_received: parseFloat(row.quantity_received),
          location_id: row.line_item.item_type === 'chemical' && row.location_id ? parseInt(row.location_id) : null,
          batch_number: row.batch_number.trim() || null,
          expiry_date: row.expiry_date || null,
          barcode_scanned: row.barcode_scanned.trim() || null,
          unit_cost: row.unit_cost ? parseFloat(row.unit_cost) : null,
          notes: row.notes.trim() || null,
          ...(row.line_item.item_type === 'nursery'
            ? { nursery_location_id: parseInt(row.nursery_location_id, 10) }
            : {}),
        })),
      });

      if (res.success) {
        onReceived();
      } else {
        const msg = (res as { error?: { message?: string } }).error?.message;
        setError(msg ?? 'Failed to record receipt. Please try again.');
      }
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : 'Failed to record receipt. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const outstandingItems = rows.filter(r => r.include);
  const mismatchCount = outstandingItems.filter(r => r.barcode_check?.status === 'mismatch').length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-40 p-0 sm:p-4">
      <div className="bg-white w-full sm:rounded-2xl sm:max-w-2xl max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-green-600" />
            <h2 className="text-base font-semibold text-gray-900">Receive Stock</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {rows.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">All items on this PO have been fully received.</p>
          ) : (
            <>
              {/* Receipt notes */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Receipt Notes (optional)</label>
                <input
                  type="text"
                  value={receiptNotes}
                  onChange={e => setReceiptNotes(e.target.value)}
                  placeholder="Delivery notes, condition of goods..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              {hasNursery && (
                <div>
                  <label htmlFor="receive-freight" className="block text-xs font-medium text-gray-700 mb-1">Freight on this delivery, ex GST</label>
                  <input
                    id="receive-freight"
                    type="text"
                    inputMode="decimal"
                    value={freight}
                    onChange={e => setFreight(e.target.value)}
                    placeholder="None"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Shared over the plants and merchandise received by value, so each lot carries what it really cost.</p>
                </div>
              )}
              {/* Line item rows */}
              {rows.map(row => (
                <div
                  key={row.line_item_id}
                  className={`border rounded-xl p-4 transition-colors ${row.include ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50 opacity-60'}`}
                >
                  {/* Item header with checkbox */}
                  <div className="flex items-start gap-3 mb-3">
                    <input
                      type="checkbox"
                      checked={row.include}
                      onChange={e => updateRow(row.line_item_id, { include: e.target.checked })}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {row.line_item.item_type === 'chemical'
                          ? (row.line_item.product_name ?? 'Unknown chemical')
                          : row.line_item.item_description}
                      </p>
                      <p className="text-xs text-gray-400">
                        Outstanding: {outstandingOf(row.line_item)} {row.line_item.unit}
                      </p>
                    </div>
                  </div>

                  {row.include && (
                    <div className="space-y-3">
                      {/* Qty received */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Qty Received ({row.line_item.unit})
                          </label>
                          <input
                            type="number"
                            min="0.001"
                            step="any"
                            value={row.quantity_received}
                            onChange={e => updateRow(row.line_item_id, { quantity_received: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                          {overReceived(row) && (
                            <p className="text-xs text-amber-700 mt-1">
                              More than the {outstandingOf(row.line_item)} {row.line_item.unit} outstanding
                            </p>
                          )}
                        </div>
                        {row.line_item.item_type === 'nursery' && (
                          <div>
                            <label htmlFor={`receive-place-${row.line_item_id}`} className="block text-xs text-gray-600 mb-1">Put it in</label>
                            <select
                              id={`receive-place-${row.line_item_id}`}
                              value={row.nursery_location_id}
                              onChange={e => updateRow(row.line_item_id, { nursery_location_id: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                              <option value="">Choose a place…</option>
                              {places.map(l => (
                                <option key={l.id} value={l.id}>
                                  {l.name} · {LOCATION_TYPE_LABEL[l.location_type] ?? l.location_type}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {row.line_item.item_type === 'chemical' && (
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Storage Location</label>
                            <select
                              value={row.location_id}
                              onChange={e => updateRow(row.line_item_id, { location_id: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                              <option value="">Select location...</option>
                              {locations.filter(l => l.is_active).map(l => (
                                <option key={l.id} value={l.id}>
                                  {l.farm_name ? `${l.farm_name} — ${l.name}` : l.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      {row.line_item.item_type === 'nursery' && (
                        <div>
                          <label htmlFor={`receive-cost-${row.line_item_id}`} className="block text-xs text-gray-600 mb-1">Cost each, ex GST</label>
                          <input
                            id={`receive-cost-${row.line_item_id}`}
                            type="text"
                            inputMode="decimal"
                            value={row.unit_cost}
                            onChange={e => updateRow(row.line_item_id, { unit_cost: e.target.value })}
                            placeholder="As ordered"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                      )}
                      {/* Batch/expiry only make sense for chemical stock */}
                      {row.line_item.item_type === 'chemical' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Batch Number</label>
                            <input
                              type="text"
                              value={row.batch_number}
                              onChange={e => updateRow(row.line_item_id, { batch_number: e.target.value })}
                              placeholder="Optional"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Expiry Date</label>
                            <input
                              type="date"
                              value={row.expiry_date}
                              onChange={e => updateRow(row.line_item_id, { expiry_date: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                          </div>
                        </div>
                      )}

                      {/* Barcode — recorded for every item type; only chemicals
                          get checked against the registry. */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Barcode</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={row.barcode_scanned}
                            onChange={e => updateRow(row.line_item_id, { barcode_scanned: e.target.value, barcode_check: null })}
                            onBlur={e => verifyBarcode(row.line_item_id, e.target.value)}
                            placeholder="Scan or enter barcode..."
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                          <button
                            type="button"
                            onClick={() => setScanningRowId(row.line_item_id)}
                            className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                          >
                            <Camera className="h-4 w-4" />
                            Scan
                          </button>
                        </div>
                        {row.barcode_check && (
                          <div className={`mt-1.5 flex items-start gap-1.5 text-xs ${BARCODE_CHECK_STYLES[row.barcode_check.status]}`}>
                            {row.barcode_check.status === 'checking' && <Loader2 className="h-3.5 w-3.5 shrink-0 mt-px animate-spin" />}
                            {row.barcode_check.status === 'match' && <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-px" />}
                            {row.barcode_check.status === 'mismatch' && <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />}
                            {row.barcode_check.status === 'unknown' && <Info className="h-3.5 w-3.5 shrink-0 mt-px" />}
                            <span>
                              {row.barcode_check.status === 'checking' ? 'Checking barcode...' : row.barcode_check.message}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      <div>
                        <input
                          type="text"
                          value={row.notes}
                          onChange={e => updateRow(row.line_item_id, { notes: e.target.value })}
                          placeholder="Item notes (optional)"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-green-400"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {mismatchCount > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {mismatchCount === 1
                  ? "1 scanned barcode doesn't match the product on its line."
                  : `${mismatchCount} scanned barcodes don't match the products on their lines.`}{' '}
                Check the delivery before receiving — you can still proceed if a substitute was supplied.
              </span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Footer actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            {rows.length > 0 && (
              <button
                type="submit"
                disabled={submitting || outstandingItems.length === 0}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Recording...' : `Receive ${outstandingItems.length} Item${outstandingItems.length !== 1 ? 's' : ''}`}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Barcode scanner overlay */}
      {scanningRowId !== null && (
        <BarcodeScanner
          label={po.line_items.find(li => li.id === scanningRowId)?.item_type === 'nursery' ? 'Scan the tag barcode' : 'Scan Chemical Barcode'}
          onScan={barcode => handleBarcodeScanned(scanningRowId, barcode)}
          onClose={() => setScanningRowId(null)}
        />
      )}
    </div>
  );
}
